// ══════════════════════════════════════════════════════════════════════
//  MIFUMO YA KUPANGA MECHI (Pairing Systems)
//  Injini moja inayotumiwa na Mashindano ya Ofisini NA Matukio ya IFR,
//  ili kanuni zisitofautiane kati ya sehemu mbili za App.
//
//  Kila mfumo unachukua orodha ya "washindani" wenye umbo lilelile:
//    { id, name, points, whiteCount, blackCount }
//  (hii ni umbo linalotoka kwenye computeStandings ya kila upande)
//  na historia ya michezo iliyopita (kuepuka rematch, kusawazisha bye),
//  kisha inarudisha pairings za raundi inayofuata.
// ══════════════════════════════════════════════════════════════════════

export function pairKey(a, b) { return [a, b].sort().join('__'); }

function assignColors(a, b) {
  const balA = a.whiteCount - a.blackCount;
  const balB = b.whiteCount - b.blackCount;
  let white = a, black = b;
  if (balA > balB) { white = b; black = a; }
  else if (balB > balA) { white = a; black = b; }
  return {
    whiteId: white.id, whiteName: white.name,
    blackId: black.id, blackName: black.name,
    result: '',
  };
}

// ══ SWISS — kwa vikundi vikubwa (hadi mamia ya wachezaji) ═══════════════
// Kanuni: wenye pointi sawa wanacheza wao kwa wao; hakuna rematch isipokuwa
// haiwezekani kuepuka; rangi zinasawazishwa; bye inazunguka kwa haki.
export function swissPairing({ standings, playedPairs, byeHistory }) {
  if (standings.length < 2) {
    return { pairings: [], byeId: null, byeName: null, error: 'Washindani hawatoshi.' };
  }

  let pool = [...standings];
  const order = new Map(pool.map((p, i) => [p.id, i]));

  let bye = null;
  if (pool.length % 2 !== 0) {
    const fromBottom = [...pool].reverse();
    bye = fromBottom.find(p => !byeHistory.has(p.id)) || fromBottom[0];
    pool = pool.filter(p => p.id !== bye.id);
  }

  const brackets = [];
  let curScore = null, curGroup = [];
  pool.forEach(p => {
    if (p.points !== curScore) {
      if (curGroup.length) brackets.push(curGroup);
      curGroup = []; curScore = p.points;
    }
    curGroup.push(p);
  });
  if (curGroup.length) brackets.push(curGroup);

  const pairings = [];
  let floaters = [];

  brackets.forEach(bracket => {
    const group = [...floaters, ...bracket]
      .sort((a, b) => (order.get(a.id) ?? 999) - (order.get(b.id) ?? 999));
    floaters = [];

    const used = new Set();
    for (let i = 0; i < group.length; i++) {
      const a = group[i];
      if (used.has(a.id)) continue;
      let opp = group.slice(i + 1).find(b => !used.has(b.id) && !playedPairs.has(pairKey(a.id, b.id)));
      if (!opp) opp = group.slice(i + 1).find(b => !used.has(b.id));
      if (opp) {
        used.add(a.id); used.add(opp.id);
        pairings.push(assignColors(a, opp));
      } else {
        floaters.push(a);
      }
    }
  });

  while (floaters.length >= 2) {
    pairings.push(assignColors(floaters.shift(), floaters.shift()));
  }
  if (floaters.length === 1 && !bye) bye = floaters[0];

  return { pairings, byeId: bye?.id || null, byeName: bye?.name || null };
}

// ══ ROUND ROBIN — kila mtu na kila mtu (vikundi vidogo, watu 4–16) ══════
// Njia ya "duara" (circle method): mtu wa kwanza anabaki pale pale,
// wengine wanazunguka kila raundi. Kwa n wachezaji: raundi n-1 (n hata)
// au n (n isiyo ya kawaida, mmoja anapumzika kila raundi kwa zamu).
export function roundRobinSchedule(participants) {
  if (participants.length < 3) {
    return { rounds: [], error: 'Round Robin inahitaji washindani angalau 3.' };
  }
  let ids = participants.map(p => ({ id: p.id, name: p.name }));
  const hasBye = ids.length % 2 !== 0;
  if (hasBye) ids.push(null);

  const n = ids.length;
  const fixed = ids[0];
  let rotating = ids.slice(1);
  const rounds = [];
  let colorFlip = false;

  for (let r = 0; r < n - 1; r++) {
    const arranged = [fixed, ...rotating];
    const pairings = [];
    let byeEntry = null;

    for (let i = 0; i < n / 2; i++) {
      const a = arranged[i];
      const b = arranged[n - 1 - i];
      if (a === null) { byeEntry = b; continue; }
      if (b === null) { byeEntry = a; continue; }
      // Rangi zinabadilishana kila raundi ili isawazike kwa muda mrefu
      const white = colorFlip ? b : a;
      const black = colorFlip ? a : b;
      pairings.push({
        whiteId: white.id, whiteName: white.name,
        blackId: black.id, blackName: black.name,
        result: '',
      });
    }
    rounds.push({ pairings, byeId: byeEntry?.id || null, byeName: byeEntry?.name || null });
    colorFlip = !colorFlip;
    rotating.unshift(rotating.pop());
  }

  return { rounds };
}

// ══ KNOCKOUT — kuondoana (bracket), kwa vikundi vidogo hadi vya kati ═══
// Wenye nafasi za juu (seed) wanapangiwa "bye" raundi ya kwanza kama
// idadi si nguvu-mbili-kamili (power of 2). Mshindi wa kila mechi
// anaendelea raundi inayofuata; aliyeshindwa anatoka.
export function knockoutBracket(participantsSeeded) {
  const n = participantsSeeded.length;
  if (n < 2) return { rounds: [], error: 'Knockout inahitaji washindani angalau 2.' };

  let bracketSize = 1;
  while (bracketSize < n) bracketSize *= 2;

  // Mpangilio wa kawaida wa bracket (1 dhidi ya wa mwisho, 2 dhidi ya
  // wa pili kutoka mwisho, n.k.) ili wenye nafasi za juu wasikutane mapema
  const slots = new Array(bracketSize).fill(null);
  const order = standardSeedOrder(bracketSize);
  order.forEach((seedPos, slotIndex) => {
    slots[slotIndex] = participantsSeeded[seedPos - 1] || null; // null = bye
  });

  const round1 = [];
  for (let i = 0; i < bracketSize; i += 2) {
    const a = slots[i], b = slots[i + 1];
    if (a && b) {
      round1.push({
        whiteId: a.id, whiteName: a.name,
        blackId: b.id, blackName: b.name,
        result: '',
      });
    } else {
      // Bye — aliyepo peke yake anaendelea moja kwa moja bila mchezo
      round1.push({
        whiteId: (a || b).id, whiteName: (a || b).name,
        blackId: null, blackName: 'BYE', result: '1-0', isBye: true,
      });
    }
  }

  return { rounds: [{ number: 1, pairings: round1 }], bracketSize };
}

// Mpangilio wa kawaida wa "seed" kwenye bracket (1 vs N, 2 vs N-1, n.k.)
function standardSeedOrder(size) {
  let order = [1, 2];
  while (order.length < size) {
    const next = [];
    const sum = order.length * 2 + 1;
    order.forEach(s => { next.push(s); next.push(sum - s); });
    order = next;
  }
  return order;
}

// Raundi inayofuata ya knockout — kutoka washindi wa raundi iliyopita
export function knockoutNextRound(previousPairings) {
  const winners = previousPairings.map(p => {
    if (p.isBye) return { id: p.whiteId, name: p.whiteName };
    if (p.result === '1-0') return { id: p.whiteId, name: p.whiteName };
    if (p.result === '0-1') return { id: p.blackId, name: p.blackName };
    return null; // bado hakuna matokeo au sare (knockout haina sare — inahitaji tie-break)
  });
  if (winners.some(w => !w)) {
    return { pairings: [], error: 'Kamilisha matokeo yote ya raundi hii kwanza (knockout haina sare — chagua mshindi).' };
  }
  const pairings = [];
  for (let i = 0; i < winners.length; i += 2) {
    const a = winners[i], b = winners[i + 1];
    if (!b) {
      pairings.push({ whiteId: a.id, whiteName: a.name, blackId: null, blackName: 'BYE', result: '1-0', isBye: true });
    } else {
      pairings.push({ whiteId: a.id, whiteName: a.name, blackId: b.id, blackName: b.name, result: '' });
    }
  }
  return { pairings };
}
